import z from "zod";
import labels from "./labels";

const localLabel = (...args) => labels.getLocalLabel(...args);

export class IntegrifyUIError extends Error { }

/**
 * @typedef {z.infer<typeof serializedIntegrifyErrorSchema>} SerializedIntegrifyError
 */

const serializedIntegrifyErrorSchema = z.object({
  message: z.string(),
  code: z.union([z.string(), z.number()]),
  status: z.number(),
  statusCode: z.number(),
  description: z.string().optional(),
  stack: z.string().optional(),
  debugInfo: z.object({}).passthrough().optional(),
  errorGuid: z.string(),
});

function safeParseInt(input) {
  const result = parseInt(input);
  if (isNaN(result)) return null;
  if (typeof input === "string" && result.toString() !== input) return null;
  if (typeof input === "number" && result !== input) return null;
  return result;
}

function parseErrorCode(code) {
  if (typeof code === "string")
    return code
      .split("-")
      .map((c) => safeParseInt(c) ?? errorCodes.UNKNOWN_ERROR);
  if (typeof code === "number") return [code];
  return [errorCodes.UNKNOWN_ERROR];
}

export function hasErrorCode(responseObj, code) {
  const parsed = safeParseInt(code);
  if (!parsed) {
    throw new Error("Invalid error code", code);
  }

  const codes = parseErrorCode(responseObj.code);
  return codes.includes(parsed);
}

/**
 *
 * @param {*} responseObj
 * @returns {responseObj is SerializedIntegrifyError}
 */
export function isSerializedIntegrifyError(responseObj) {
  const result = serializedIntegrifyErrorSchema.safeParse(responseObj);
  if (result.success) return true;
}

/**
 * @param {unknown} err
 * @param {string} defaultErrMsg
 * @returns {string}
 */
export function getErrorMessage(err, defaultErrMsg = "Unknown error") {
  if (err instanceof IntegrifyUIError) {
    return localLabel(err.message);
  }

  const responseObj =
    err?.response?.body ?? err?.response?.obj ?? err?.response?.data;
  let errMsg;
  if (isSerializedIntegrifyError(responseObj)) {
    errMsg = responseObj.message || responseObj.description;
  } else {
    // statusText is missing on HTTP/2
    errMsg =
      err?.response?.statusText ||
      getStatusText(err?.response?.status) ||
      (typeof err === "string" ? err : defaultErrMsg);
  }

  return localLabel(errMsg);
}

export function getErrorMessageWithoutCode(error, defaultMessage = 'An unexpected error occurred.') {

  // Commenting this out because integrify-libs is not added to single-spa-portal
  // return localLabel(getUIFriendlyMessage(error, defaultMessage));

  // Recreating the logic from 'getUIFriendlyMessage' in integrify-libs here
  try {

    if (error instanceof IntegrifyUIError) {
      return localLabel(error.message);
    }

    let responseObj = error?.response?.body || error?.response?.obj || error?.response?.data;

    let errMsg;
    if (isSerializedIntegrifyError(responseObj)) {
      const prefix = `${responseObj.code}: `;
      errMsg = responseObj.message.startsWith(prefix)
        ? responseObj.message.slice(prefix.length)
        : responseObj.message;
    } else {
      // statusText is missing on HTTP/2
      errMsg =
        err?.response?.statusText ||
        getStatusText(err?.response?.status) ||
        defaultErrMsg;
    }

    return localLabel(errMsg || defaultMessage);
  } catch {
    return localLabel(defaultMessage || error?.message);
  }
}

/**
 * Originally taken from https://blog.uuid.rocks/how-to-convert-http-status-codes-to-statustext-in-javascript/
 */
function getStatusText(code) {
  return {
    100: "Continue",
    101: "Switching Protocols",
    102: "Processing",
    200: "OK",
    201: "Created",
    202: "Accepted",
    203: "Non-authoritative Information",
    204: "No Content",
    205: "Reset Content",
    206: "Partial Content",
    207: "Multi-Status",
    208: "Already Reported",
    226: "IM Used",
    300: "Multiple Choices",
    301: "Moved Permanently",
    302: "Found",
    303: "See Other",
    304: "Not Modified",
    305: "Use Proxy",
    307: "Temporary Redirect",
    308: "Permanent Redirect",
    400: "Bad Request",
    401: "Unauthorized",
    402: "Payment Required",
    403: "Forbidden",
    404: "Not Found",
    405: "Method Not Allowed",
    406: "Not Acceptable",
    407: "Proxy Authentication Required",
    408: "Request Timeout",
    409: "Conflict",
    410: "Gone",
    411: "Length Required",
    412: "Precondition Failed",
    413: "Payload Too Large",
    414: "Request-URI Too Long",
    415: "Unsupported Media Type",
    416: "Requested Range Not Satisfiable",
    417: "Expectation Failed",
    418: "I'm a teapot",
    421: "Misdirected Request",
    422: "Unprocessable Entity",
    423: "Locked",
    424: "Failed Dependency",
    426: "Upgrade Required",
    428: "Precondition Required",
    429: "Too Many Requests",
    431: "Request Header Fields Too Large",
    444: "Connection Closed Without Response",
    451: "Unavailable For Legal Reasons",
    499: "Client Closed Request",
    500: "Internal Server Error",
    501: "Not Implemented",
    502: "Bad Gateway",
    503: "Service Unavailable",
    504: "Gateway Timeout",
    505: "HTTP Version Not Supported",
    506: "Variant Also Negotiates",
    507: "Insufficient Storage",
    508: "Loop Detected",
    510: "Not Extended",
    511: "Network Authentication Required",
    599: "Network Connect Timeout Error",
  }[code];
}
